import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'

import { Box } from '../box'
import { Text, type TextProps } from '../text'
import { HelpLegendDot, type HelpLegendDotShape } from './HelpLegendDot'

export type HelpLegendItemOrientation = 'column' | 'row'

export type HelpLegendItemProps = {
    /** Title / primary label (already translated). */
    title: string
    /** Optional secondary description (already translated). */
    subtitle?: string
    /**
     * Marker colour for the default {@link HelpLegendDot} (runtime/data-driven
     * hex, e.g. `colors.graphics.teal`). Ignored when `markerSlot` is set.
     */
    color?: string
    /** Marker size in px. Default 12. */
    markerSize?: number
    /** Marker shape. Default `square`. */
    markerShape?: HelpLegendDotShape
    /**
     * Custom marker node (e.g. a baggage indicator icon), rendered instead of the
     * coloured dot — for icon-based legends such as the baggage flight table.
     */
    markerSlot?: React.ReactNode
    /**
     * Optional runtime colour for the title text (e.g. the delivery-time legend
     * where the value is tinted to match its marker). Defaults to the `default`
     * content ink.
     */
    titleColor?: string
    /** Title font weight. Default `medium` (Figma help labels are P3-Medium). */
    titleWeight?: TextProps['weight']
    /** Title font size. Default `body` (baggage legend); `caption` for the
     * Mobile/StatusArrivals row (Figma P4-Medium 12). */
    titleSize?: TextProps['size']
    /** `column` (centred, default) or `row` (inline marker + text). */
    orientation?: HelpLegendItemOrientation
    /**
     * Text alignment. `default` keeps the orientation's built-in alignment
     * (centred column / inline row); `start` forces the title + subtitle to the
     * left, filling the remaining row width.
     */
    align?: 'default' | 'start'
    className?: string
    testID?: string
}

const containerStyle = tva({
    base: 'gap-2',
    variants: {
        orientation: {
            column: 'flex-1 items-center',
            row: 'flex-row items-center',
        },
    },
    defaultVariants: {
        orientation: 'column',
    },
})

const textWrapStyle = tva({
    base: 'gap-1',
    variants: {
        orientation: {
            column: 'items-center',
            row: '',
        },
    },
    defaultVariants: {
        orientation: 'column',
    },
})

const labelStyle = tva({
    base: '',
    variants: {
        orientation: {
            column: 'text-center',
            row: '',
        },
    },
    defaultVariants: {
        orientation: 'column',
    },
})

/**
 * A single entry in a dashboard Help legend: a coloured marker (dot/swatch or a
 * custom icon slot) with a title and optional subtitle. Universal replacement
 * for the legacy `LegendDotOnHelp`. `column` lays the marker above centred text
 * (the colours legend); `row` puts the marker beside the text (icon + value
 * legends like the baggage flight table).
 */
export const HelpLegendItem: React.FC<HelpLegendItemProps> = ({
    title,
    subtitle,
    color,
    markerSize,
    markerShape,
    markerSlot,
    titleColor,
    titleWeight = 'medium',
    titleSize = 'body',
    orientation = 'column',
    align = 'default',
    className,
    testID,
}) => {
    const marker =
        markerSlot ??
        (color !== undefined && (
            <HelpLegendDot color={color} size={markerSize} shape={markerShape} />
        ))

    const startAlign = align === 'start'
    const textWrapCls = `${textWrapStyle({ orientation })} ${startAlign ? 'flex-1 items-start' : ''}`
    const labelCls = `${labelStyle({ orientation })} ${startAlign ? 'w-full text-left' : ''}`

    return (
        <Box className={containerStyle({ orientation, class: className })} testID={testID}>
            {marker}
            <Box className={textWrapCls}>
                <Text
                    size={titleSize}
                    weight={titleWeight}
                    tone='default'
                    color={titleColor}
                    className={labelCls}
                >
                    {title}
                </Text>
                {subtitle && (
                    <Text size='caption' tone='muted' className={labelCls}>
                        {subtitle}
                    </Text>
                )}
            </Box>
        </Box>
    )
}

HelpLegendItem.displayName = 'HelpLegendItem'
